/**
 * i18n - Internationalization module for WoWSim WotLK
 *
 * Provides comprehensive translation support for the UI with:
 * - Locale management with localStorage persistence
 * - Translation function with namespace support
 * - Automatic browser language detection
 * - Event-driven updates on language change
 *
 * @example
 * ```typescript
 * import { t, getI18n } from './i18n';
 *
 * // Basic translation
 * const label = t('sim.iterations');
 *
 * // With parameters
 * const message = t('common.welcome', { name: 'Player' });
 *
 * // Change locale
 * getI18n().setLocale(eventId, 'zh-CN');
 * ```
 */

import { Class } from '../proto/common.js';
import { t as getTranslation } from './i18n.js';

/**
 * Maps Class enum to lowercase class name strings for translation keys.
 */
const classKeyNames: Record<Class, string> = {
	[Class.ClassUnknown]: 'unknown',
	[Class.ClassWarrior]: 'warrior',
	[Class.ClassPaladin]: 'paladin',
	[Class.ClassHunter]: 'hunter',
	[Class.ClassRogue]: 'rogue',
	[Class.ClassPriest]: 'priest',
	[Class.ClassDeathknight]: 'deathknight',
	[Class.ClassShaman]: 'shaman',
	[Class.ClassMage]: 'mage',
	[Class.ClassWarlock]: 'warlock',
	[Class.ClassDruid]: 'druid',
};

/**
 * Gets the lowercase class name key for translation lookups.
 * @param playerClass The Class enum value
 * @returns Lowercase class name string (e.g., 'warrior', 'mage')
 */
export function getClassKeyName(playerClass: Class): string {
	return classKeyNames[playerClass] || 'unknown';
}

/**
 * Gets the localized name for a talent tree.
 * @param className The class name (e.g., 'warrior', 'mage')
 * @param treeName The English tree name (e.g., 'Arms', 'Frost')
 * @returns The localized tree name
 */
export function getTalentTreeName(className: string, treeName: string): string {
	const key = `talents.trees.${className}.${treeName.replace(/\s+/g, '')}`;
	return getTranslation(key);
}

export { getI18n, initI18n, t, I18nManager } from './i18n.js';
export type { Locale, TranslationMessages, I18nConfig } from './i18n.js';

// Re-export helper functions from proto_utils/names for convenience
export { getStatName, getRaceName, getClassName, getProfessionName } from '../proto_utils/names.js';
